import {TooltipBaseModel} from "../tooltip-base/tooltip-base.model";
import {action, makeObservable, observable, reaction, runInAction} from "mobx";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IOnBoardingTooltipsContainer} from "./on-boarding-tooltips-container.interface";
import {IOnBoardingTooltipOptions} from "./on-boarding-tooltip-options.interface";
import {Check} from "@sparkware/app-utils";


export abstract class OnBoardingTooltipModel extends TooltipBaseModel<IOnBoardingTooltipOptions> {
    constructor(protected readonly onBoardingTooltipSContainer: IOnBoardingTooltipsContainer,
                services: IServiceFactoryExtended,
                options: IOnBoardingTooltipOptions) {
        super(services, options);
        makeObservable(this);
        this._loadPersistedValuesFromLocalStorage();

        reaction(() => this.services.user.isAuthorized, (isAuthorized) => {
            if(!isAuthorized) {
                this.sessionDisplayCount = 0;
            }
        }, {
            fireImmediately: true
        });

        this.services.appLauncher.then(appLauncher => {
            reaction(() => appLauncher.isThereRunningApps, (isThereRunningApps) => {
                this._isThereRunningApps = isThereRunningApps;
            }, {
                fireImmediately: true
            })
        });

        this.services.gameRunner.then(gameRunner => {
            reaction(() => gameRunner.currentRunningGame, (currentRunningGame) => {
                this._isGameRunning = Boolean(currentRunningGame);
            }, {
                fireImmediately: true
            })
        });
    }

    @observable.ref
    private _isThereRunningApps = true;

    @observable.ref
    private _isGameRunning = true;

    protected abstract _getElementToAttach(): HTMLElement | null;
    
    shouldDisplay(): boolean {
        const orbitOverlayConsumersCount = this.services.overlay.countCurrentConsumers(consumer => {
            if(consumer === this.onBoardingTooltipSContainer) {
                return false;
            }

            return consumer.needsOverlay;
        });

        const isOnBoardingTooltipsEnabled = this.onBoardingTooltipSContainer.isOnBoardingEnabled;
        const isAuthorized = this.services.user.isAuthorized;
        const sdkHasOpenOverlays = this.services.sdk.hasOpenOverlays;
        const areRunningApps = this._isThereRunningApps;
        const areOpenDialogs = this.services.dialog.hasOpenDialogs;
        const isOrbitRoute = this.services.routing.isOrbitRoute(this.services.location.currentUri.pathName);
        const currentRoutePreventsNotifications = Boolean(this.services.routing.currentRoute.data?.preventNotifications);
        const isGameRunning = this._isGameRunning;
        const hasOpenToasters = this.services.toaster.hasOpenToasters;

        //The reason we put the values in those variables is to make sure that this method depends on all these observables.

        return isOnBoardingTooltipsEnabled
                && isAuthorized
                && isOrbitRoute
                && orbitOverlayConsumersCount === 0
                && !sdkHasOpenOverlays
                && !areRunningApps
                && !areOpenDialogs
                && !currentRoutePreventsNotifications
                && !this._hasReachedMaximumDisplayCount
                && !this._wasDisplayedOnCurrentSession
                && !isGameRunning
                && !hasOpenToasters;
    }


    closeTooltip(): void {
        super.closeTooltip();
        this.totalDisplayCount++;
        this.sessionDisplayCount++;
    }

    onToolTipRendered(tooltipContainer: HTMLElement) {
        super.onToolTipRendered(tooltipContainer);
    }

    @action
    private _loadPersistedValuesFromLocalStorage(): void {
        let storageValue = this.services.localStorage.getItem(this._totalDisplayCountLocalStorageKey);
        if(storageValue) {
            this._totalDisplayCount = parseInt(storageValue);
        }

        storageValue = this.services.localStorage.getItem(this._sessionDisplayCountLocalStorageKey);
        if(storageValue) {
            this._sessionDisplayCount = parseInt(storageValue);
        }
    }

    private get _totalDisplayCountLocalStorageKey(): string {
        return `${this.options.localStorageKey}.totalDisplayCount`;
    }

    private get _sessionDisplayCountLocalStorageKey(): string {
        return `${this.options.localStorageKey}.sessionDisplayCount`;
    }

    @observable.ref
    private _totalDisplayCount: number = 0;
    get totalDisplayCount(): number {
        if(Check.isNullOrUndefined(this.options.maximumDisplayCount)) {
            return 0;
        }

        return this._totalDisplayCount;

    }
    set totalDisplayCount(value: number) {
        if(!Check.isNullOrUndefined(this.options.maximumDisplayCount)) {
            runInAction(() => this._totalDisplayCount = value);
            this.services.localStorage.setItem(this._totalDisplayCountLocalStorageKey, value);
        }
    }

    @observable.ref
    private _sessionDisplayCount: number = 0;

    get sessionDisplayCount(): number {
        return this._sessionDisplayCount;
    }

    set sessionDisplayCount(value: number) {
        runInAction(() => this._sessionDisplayCount = value);
        this.services.localStorage.setItem(this._sessionDisplayCountLocalStorageKey, value);
    }


    protected get _hasReachedMaximumDisplayCount(): boolean {
        if(Check.isNullOrUndefined(this.options.maximumDisplayCount)) {
            return false;
        }
        return this.totalDisplayCount >= this.options.maximumDisplayCount;
    }

    protected get _wasDisplayedOnCurrentSession(): boolean {
        return this.sessionDisplayCount >= 1;
    }


}